import { FC, useCallback, useEffect, useState } from 'react';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {
  DataTableExpandedRows,
  DataTableValueArray,
} from 'primereact/datatable';
import dayjs from 'dayjs';
import { TratamentoSementesFormType } from '../types';
import { Form } from '../form-controller';
import TratamentoDeSementesParametrosHead from './head';
import AutoSortTable from '../../../../components/AutoSortTable';
import {
  DefensivoFormValue,
  Tratamento,
  TratamentoFormValue,
  Unidade,
} from '../../../../@types';
import TratamentoDeSementesParametrosDialogForm from './tratamento-dialog';
import TratamentoDeSementesParametrosRowExpansion from './row-expansion';
import useAsyncFetch from '../../../../hooks/use-async';
import {
  ItensEstoqueService,
  ItensSaldoService,
  UnidadesService,
} from '../../../../services';
import {
  humanizePesoComPms,
  getTratamentoIdentification,
  isSegundoTratamento,
} from '../utils';
import convertUnidade from '../../../../utils/convert-unidade';
import defaultColumnProps from '../../../../utils/default-column-props';
import humanizeDate from '../../../../utils/humanize-date';
import Agro1Toast from '../../../../components/Agro1Toast';

type ExpandedRows = DataTableExpandedRows | DataTableValueArray;

const getQuantidadeValueTratamento = (
  tratamento: Tratamento,
  unidade?: Unidade,
  segundoTratamento?: boolean,
) =>
  segundoTratamento ||
  tratamento.quantidadeTotalSementes === undefined ||
  !unidade
    ? undefined
    : tratamento.quantidadeTotalSementes / unidade.unidadeConversao.taxa;

const TratamentoDeSementesParametros: FC<TratamentoSementesFormType> = ({
  formObject,
  tratamentoSementes,
}) => {
  const { watch, setValue } = formObject.form;
  const [atividade, tratamentos, propriedade] = watch([
    'atividade',
    'tratamentos',
    'propriedade',
  ]);

  const [dialogFormVisible, setDialogFormVisible] = useState<boolean>(false);
  const [selectedTratamento, setSelectedTratamento] = useState<
    string | undefined
  >(undefined);
  const [expandedRows, setExpandedRows] = useState<ExpandedRows | undefined>(
    undefined,
  );

  const { data: listaSementesDeOrigem } = useAsyncFetch(
    {
      callback: () => {
        if (!propriedade) return undefined;

        return ItensSaldoService.saldos({
          operacaoItem: 'cultivar',
          propriedadeId: propriedade.id,
          sementeValidaTratamento: true,
          noPaginate: true,
        });
      },
      errorMessage: 'Falha ao carregar as sementes de origem.',
      withLoader: true,
    },
    [propriedade],
  );

  const { data: listaSementesDeDestino } = useAsyncFetch(
    {
      callback: () => {
        if (!propriedade) return undefined;

        return ItensEstoqueService.estoques({
          operacaoItem: 'cultivar',
          propriedadeId: propriedade.id,
          noPaginate: true,
        });
      },
      errorMessage: 'Falha ao carregar as sementes de destino.',
      withLoader: true,
    },
    [propriedade],
  );

  const { data: listaDefensivos } = useAsyncFetch(
    {
      callback: () => {
        if (!propriedade) return undefined;

        return ItensSaldoService.saldos({
          operacaoItem: 'pulverizacao',
          propriedadeId: propriedade.id,
          noPaginate: true,
        });
      },
      errorMessage: 'Falha ao carregar os defensivos.',
      withLoader: true,
    },
    [propriedade],
  );

  const { data: listaUnidades } = useAsyncFetch(
    {
      callback: () => UnidadesService.list(),
      errorMessage: 'Falha ao carregar as unidades.',
      withLoader: true,
    },
    [propriedade],
  );

  const openDialogForm = useCallback((item?: TratamentoFormValue) => {
    setSelectedTratamento(item?.identification);
    setDialogFormVisible(true);
  }, []);

  const closeDialogForm = useCallback(() => {
    setDialogFormVisible(false);
    setSelectedTratamento(undefined);
  }, []);

  const dialogFormSubmit = useCallback(
    (value: TratamentoFormValue) => {
      const statesHandler = (newTratamentos: TratamentoFormValue[]) => {
        if (value.identification) {
          setValue('tratamentos', newTratamentos);
          const newExpandedRows: ExpandedRows = {
            ...expandedRows,
            [value.identification]: true,
          };
          setExpandedRows(newExpandedRows);
        }
      };

      const currentTratamentos = formObject.form.getValues('tratamentos');

      if (!selectedTratamento) {
        statesHandler([...currentTratamentos, value]);
        return;
      }

      const index = currentTratamentos.findIndex(
        (item) => item.identification === selectedTratamento,
      );
      const newTratamentos = [...currentTratamentos];
      newTratamentos[index] = value;
      statesHandler(newTratamentos);

      setSelectedTratamento(undefined);
    },
    [formObject.form, selectedTratamento, setValue, expandedRows],
  );

  const removeTratamento = useCallback(
    (id?: string) => {
      const currentTratamentos = formObject.form.getValues('tratamentos');
      const newTratamentos = currentTratamentos.filter(
        (item) => item.identification !== id,
      );
      setValue('tratamentos', newTratamentos);
    },
    [setValue, formObject.form],
  );

  const rowActionsTemplate = useCallback(
    (row: TratamentoFormValue) => (
      <div className="flex justify-content-center">
        <Button
          className="mr-2 button-primary"
          type="button"
          onClick={() => openDialogForm(row)}
          icon="pi pi-pencil"
        />
        <Button
          className="button-danger"
          type="button"
          onClick={() => removeTratamento(row.identification)}
          icon="pi pi-trash"
        />
      </div>
    ),
    [openDialogForm, removeTratamento],
  );

  useEffect(() => {
    if (
      !tratamentoSementes ||
      !listaDefensivos ||
      !listaSementesDeDestino ||
      !listaSementesDeOrigem ||
      !listaUnidades
    ) {
      return;
    }

    const newTratamentos: TratamentoFormValue[] =
      tratamentoSementes.tratamentos.map((tratamento) => {
        const defensivos: DefensivoFormValue[] = tratamento.insumos.map(
          (item) => ({
            dose: item.dose,
            quantidade: item.quantidade,
            item: listaDefensivos.find(
              (defensivo) => defensivo.id === item.insumoId,
            )!,
          }),
        );

        const sementeOrigem = listaSementesDeOrigem.find(
          (semente) => semente.id === tratamento.sementeOrigemId,
        );

        const sementeDestino = listaSementesDeDestino.find(
          (semente) => semente.itemId === tratamento.sementeDestinoId,
        );

        const segundoTratamento = isSegundoTratamento({
          sementeOrigem,
          sementeDestino,
        });
        const unidade = segundoTratamento
          ? undefined
          : listaUnidades.find((item) => item.id === tratamento.unidadeId);

        return {
          dataInicio: dayjs(tratamento.dataInicio).toDate(),
          dataFim: dayjs(tratamento.dataFim).toDate(),
          copiarDeOutraSemente: undefined,
          defensivos,
          quantidade: getQuantidadeValueTratamento(
            tratamento,
            unidade,
            segundoTratamento,
          ),
          search: undefined,
          identification: getTratamentoIdentification({
            sementeOrigemId: tratamento.sementeOrigemId,
            sementeDestinoId: sementeDestino!.itemId,
            defensivos,
          }),
          sementeOrigem,
          sementeDestino,
          unidade: unidade ? convertUnidade(unidade) : undefined,
          peso: segundoTratamento ? undefined : tratamento.peso,
          pms: segundoTratamento ? undefined : tratamento.pms,
        };
      });

    setValue('tratamentos', newTratamentos);
  }, [
    listaDefensivos,
    listaSementesDeDestino,
    listaSementesDeOrigem,
    listaUnidades,
    setValue,
    tratamentoSementes,
  ]);

  useEffect(() => {
    const insumosInvalidos = tratamentos.filter(
      (tratamento) => !tratamento.sementeOrigem,
    );

    if (!insumosInvalidos.length) return;

    if (insumosInvalidos.length === 1) {
      return Agro1Toast.show({
        detail: 'Um insumo selecionado está inválido',
        severity: 'error',
        sticky: true,
      });
    }

    return Agro1Toast.show({
      detail: 'Alguns insumos selecionados estão inválidos',
      severity: 'error',
      sticky: true,
    });
  }, [tratamentos]);

  return (
    <Form formObject={formObject} onSubmit={() => null} submitByEnter={false}>
      <TratamentoDeSementesParametrosDialogForm
        visible={dialogFormVisible}
        onClose={closeDialogForm}
        selectedTratamentoId={selectedTratamento}
        formObject={formObject}
        onSubmit={dialogFormSubmit}
        listaSementesDeDestino={listaSementesDeDestino}
      />
      <div className="grid">
        <TratamentoDeSementesParametrosHead
          formObject={formObject}
          tratamentoSementes={tratamentoSementes}
        />
        <div className="xl:col-2 md:col-1 flex justify-content-end align-items-center">
          <Button
            label="Adicionar tratamento"
            className="button-secondary toggle-label"
            icon="pi pi-plus"
            iconPos="left"
            severity="success"
            type="button"
            disabled={!atividade}
            onClick={() => openDialogForm()}
          />
        </div>

        <AutoSortTable
          removableSort
          sortField="dataInicio"
          dataKey="identification"
          value={tratamentos}
          className="data-table col-10"
          emptyMessage="Nenhum tratamento adicionado, utilize o botão 'Adicionar tratamento' ao lado para começar."
          expandedRows={expandedRows}
          onRowToggle={(event) => setExpandedRows(event.data)}
          rowExpansionTemplate={TratamentoDeSementesParametrosRowExpansion}
        >
          <Column expander className="row-expander" />
          <Column
            {...defaultColumnProps()}
            header="Data Início"
            field="dataInicio"
            body={(tratamento) => humanizeDate(tratamento.dataInicio)}
          />
          <Column
            {...defaultColumnProps()}
            header="Data Término"
            field="dataFim"
            body={(tratamento) => humanizeDate(tratamento.dataFim)}
          />
          <Column
            {...defaultColumnProps('left')}
            field="sementeOrigem.descricao"
            body={(tratamento) =>
              tratamento.sementeOrigem?.descricao || 'INSUMO INDISPONÍVEL'
            }
            header="Semente de Origem"
          />
          <Column
            {...defaultColumnProps('left')}
            field="sementeDestino.descricao"
            header="Semente de Destino"
          />
          <Column
            {...defaultColumnProps()}
            field="quantidade"
            header="Qtde"
            body={(tratamento: TratamentoFormValue) =>
              isSegundoTratamento(tratamento) ? 'Tudo' : tratamento.quantidade
            }
          />
          <Column
            {...defaultColumnProps('left')}
            field="unidade.descricao"
            header="Unidade"
            body={(tratamento: TratamentoFormValue) =>
              tratamento.unidade?.descricao || '-'
            }
          />
          <Column
            {...defaultColumnProps('left')}
            header="Peso (kg)"
            field="peso"
            body={({ unidade, quantidade, pms }) =>
              humanizePesoComPms({ unidade, quantidade, pms })
            }
          />
          <Column align="center" body={rowActionsTemplate} />
        </AutoSortTable>
      </div>
    </Form>
  );
};

export default TratamentoDeSementesParametros;
